import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { CheckCircle, Spinner, ArrowRight, Sparkle, Trophy } from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';
import API_URL from '../utils/apiUrl';
import SEO from '../components/SEO';
import { trackEvent, EVT } from '../utils/analytics';
import { STARTER_ROUTES } from '../utils/starterRoutes';

const SUBJECT_BG = {
  indigo: 'bg-indigo-50 text-indigo-700 border-indigo-200 hover:bg-indigo-100 dark:bg-indigo-900/20 dark:text-indigo-300 dark:border-indigo-800',
  rose: 'bg-rose-50 text-rose-700 border-rose-200 hover:bg-rose-100 dark:bg-rose-900/20 dark:text-rose-300 dark:border-rose-800',
  emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100 dark:bg-emerald-900/20 dark:text-emerald-300 dark:border-emerald-800',
};
const SUBJECT_ICON_BG = { indigo: 'bg-indigo-500', rose: 'bg-rose-500', emerald: 'bg-emerald-500' };

export default function Welcome() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  // Two flows share /welcome:
  //   1. Parent-created-child activation via single-use token (existing flow).
  //   2. Post-verification student subject router (new — replaces /diagnostic).
  if (token) return <ChildActivationFlow token={token} />;
  return <StudentSubjectRouter />;
}

// ============================================================================
// Flow 1 — existing parent-created-child activation (token required)
// ============================================================================
function ChildActivationFlow({ token }) {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [phase, setPhase] = useState('verifying'); // 'verifying' | 'form' | 'submitting' | 'done' | 'error'
  const [childInfo, setChildInfo] = useState(null);
  const [error, setError] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/auth/welcome/verify`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token }),
        });
        if (!res.ok) {
          const d = await res.json().catch(() => ({}));
          setError(d.detail || 'This welcome link is invalid or has expired.');
          setPhase('error');
          return;
        }
        const data = await res.json();
        setChildInfo(data);
        setPhase('form');
      } catch (_e) {
        setError('Network error. Please try again.');
        setPhase('error');
      }
    })();
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    if (password !== confirm) { setError("Passwords don't match."); return; }
    setPhase('submitting');
    try {
      const res = await fetch(`${API_URL}/api/auth/welcome/set-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ token, password }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        setError(d.detail || 'Failed to set password.');
        setPhase('form');
        return;
      }
      setPhase('done');
      if (refreshUser) await refreshUser();
      setTimeout(() => navigate('/dashboard'), 1200);
    } catch (_e) {
      setError('Network error. Please try again.');
      setPhase('form');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12">
      <SEO title="Welcome" description="Activate your Grade9Ready child account." path="/welcome" noIndex />
      <div className="w-full max-w-md">
        {phase === 'verifying' && (
          <div className="neo-card text-center" data-testid="welcome-verifying">
            <Spinner size={32} className="animate-spin mx-auto mb-3 text-primary" />
            <p className="text-muted-foreground">Checking your welcome link...</p>
          </div>
        )}
        {phase === 'error' && (
          <div className="neo-card text-center" data-testid="welcome-error">
            <h1 className="font-heading font-bold text-2xl mb-2">Welcome link problem</h1>
            <p className="text-muted-foreground mb-4">{error}</p>
            <Link to="/login" className="neo-btn inline-block">Go to Login</Link>
          </div>
        )}
        {(phase === 'form' || phase === 'submitting') && childInfo && (
          <div className="neo-card" data-testid="welcome-form-card">
            <div className="flex items-center gap-2 text-primary mb-2">
              <Sparkle size={20} weight="fill" />
              <span className="text-xs font-bold uppercase tracking-wide">Welcome to Grade9Ready</span>
            </div>
            <h1 className="font-heading font-bold text-2xl mb-1">Hi {childInfo.name}!</h1>
            <p className="text-sm text-muted-foreground mb-5">
              Your parent has set up an account for you. Set a password to get started.
              <br />Login email: <strong>{childInfo.email}</strong>
            </p>
            <form onSubmit={submit} className="space-y-3" data-testid="welcome-form">
              <div>
                <label className="text-sm font-medium block mb-1">Choose a password</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="At least 8 characters"
                  className="neo-input w-full"
                  minLength={8}
                  required
                  data-testid="welcome-password"
                />
              </div>
              <div>
                <label className="text-sm font-medium block mb-1">Confirm password</label>
                <input
                  type="password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder="Type it again"
                  className="neo-input w-full"
                  minLength={8}
                  required
                  data-testid="welcome-confirm"
                />
              </div>
              {error && <p className="text-destructive text-sm" data-testid="welcome-error-msg">{error}</p>}
              <button
                type="submit"
                disabled={phase === 'submitting'}
                className="neo-btn-primary w-full py-3 font-bold flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="welcome-submit"
              >
                {phase === 'submitting' ? (
                  <><Spinner size={16} className="animate-spin" /> Setting password...</>
                ) : (
                  <>Set Password &amp; Log In <ArrowRight size={16} weight="bold" /></>
                )}
              </button>
            </form>
          </div>
        )}
        {phase === 'done' && (
          <div className="neo-card text-center" data-testid="welcome-done">
            <div className="w-16 h-16 mx-auto mb-3 rounded-full bg-emerald-500 flex items-center justify-center">
              <CheckCircle size={36} weight="fill" className="text-white" />
            </div>
            <h1 className="font-heading font-bold text-xl mb-1">You&rsquo;re in!</h1>
            <p className="text-muted-foreground">Taking you to your dashboard...</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// Flow 2 — NEW: post-signup student subject router
// ============================================================================
function StudentSubjectRouter() {
  const { user } = useAuth();

  // Read free-lesson handoff if it exists. Persists across email-verification
  // tab navigation thanks to localStorage.
  const freeLessonResult = useMemo(() => {
    try {
      const raw = localStorage.getItem('g9r_free_lesson_result');
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (parsed && parsed.completed && STARTER_ROUTES[parsed.subject]) return parsed;
      return null;
    } catch (_e) { return null; }
  }, []);

  useEffect(() => {
    trackEvent(EVT.WELCOME_VIEW, {
      from_free_lesson: Boolean(freeLessonResult),
      subject: freeLessonResult?.subject || null,
    });
  }, [freeLessonResult]);

  const onSubjectClick = (subjectKey) => {
    trackEvent(EVT.WELCOME_SUBJECT_SELECTED, { subject: subjectKey });
    trackEvent(EVT.FIRST_LESSON_OPENED, { subject: subjectKey, source: freeLessonResult ? 'free_lesson' : 'direct' });
  };

  const firstName = (user?.name || '').split(' ')[0] || 'there';

  return (
    <div className="min-h-screen px-4 py-12 bg-gradient-to-b from-primary/5 to-transparent">
      <SEO
        title="You're in — choose your starting subject"
        description="Pick a GCSE subject and jump straight into your first lesson."
        path="/welcome"
        noIndex
      />
      <div className="max-w-3xl mx-auto" data-testid="welcome-student-router">
        {/* Hero */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 rounded-full text-xs font-bold mb-3">
            <CheckCircle size={14} weight="fill" /> You&rsquo;re in — your 7-day trial is active
          </div>
          <h1 className="text-3xl sm:text-4xl font-heading font-black mb-2" data-testid="welcome-heading">
            Hi {firstName} — what do you want to improve first?
          </h1>
          <p className="text-muted-foreground max-w-xl mx-auto">
            Pick a GCSE subject and we&rsquo;ll take you straight to a useful starting lesson. You can change paths any time.
          </p>
        </div>

        {/* Free-lesson recommendation card (only if they sampled before signing up) */}
        {freeLessonResult && (
          <FreeLessonRecommendationCard
            result={freeLessonResult}
            onClick={() => onSubjectClick(freeLessonResult.subject)}
          />
        )}

        {/* Subject grid */}
        <div className="grid sm:grid-cols-3 gap-3 mb-6">
          {Object.entries(STARTER_ROUTES).map(([key, s]) => {
            const Icon = s.icon;
            return (
              <Link
                key={key}
                to={s.route}
                onClick={() => onSubjectClick(key)}
                className={`block neo-card text-left transition-all hover:-translate-y-1 border-2 ${SUBJECT_BG[s.color]}`}
                data-testid={`welcome-subject-${key}`}
              >
                <div className={`w-11 h-11 rounded-xl ${SUBJECT_ICON_BG[s.color]} text-white flex items-center justify-center mb-3 shadow-[2px_2px_0px_0px_rgb(var(--9ready-shadow))]`}>
                  <Icon size={22} weight="fill" />
                </div>
                <h3 className="font-heading font-bold text-lg mb-1 capitalize">{key}</h3>
                <p className="text-xs leading-relaxed">{s.blurb}</p>
                <div className="mt-3 flex items-center gap-1 text-xs font-bold">
                  Start {key} <ArrowRight size={12} weight="bold" />
                </div>
              </Link>
            );
          })}
        </div>

        {/* Tip + dashboard escape hatch */}
        <p className="text-center text-sm text-muted-foreground mb-4">
          Not sure? <strong>Start with Maths</strong> — it unlocks the most confidence quickly.
        </p>
        <div className="text-center">
          <Link
            to="/dashboard"
            className="text-sm text-muted-foreground hover:text-foreground underline"
            data-testid="welcome-dashboard-link"
          >
            Or go to your dashboard →
          </Link>
        </div>
      </div>
    </div>
  );
}

function FreeLessonRecommendationCard({ result, onClick }) {
  const s = STARTER_ROUTES[result.subject];
  const Icon = s.icon;
  return (
    <div className="neo-card mb-6 border-2 border-primary bg-primary/5" data-testid="welcome-free-lesson-recommendation">
      <div className="flex items-start gap-4">
        <div className={`w-12 h-12 rounded-xl ${SUBJECT_ICON_BG[s.color]} text-white flex items-center justify-center shrink-0`}>
          <Trophy size={24} weight="fill" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-black uppercase tracking-wider text-primary mb-0.5">
            Continue from your free lesson · {result.score}/{result.total} in {result.subject}
          </div>
          <h2 className="font-heading font-bold text-xl mb-1" data-testid="welcome-free-lesson-label">
            Pick up where you left off — {s.label}
          </h2>
          <p className="text-sm text-muted-foreground mb-4">
            We&rsquo;ll drop you straight into the full {result.subject} pathway, starting with the topic that builds on what you just tried.
          </p>
          <Link
            to={s.route}
            onClick={onClick}
            className="neo-btn inline-flex items-center gap-2"
            data-testid="welcome-free-lesson-cta"
          >
            Continue with {result.subject} <ArrowRight size={16} weight="bold" />
          </Link>
        </div>
      </div>
    </div>
  );
}
